import React from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { STAGES, statusToIndex } from "../orderStages";

function StageIcon({ done, current }) {
    if (done) return <CheckCircle2 className="w-4 h-4" />;
    if (current) return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    return <span className="w-2 h-2 rounded-full bg-current opacity-50" />;
}

function stageDotClass(done, current) {
    if (done) return "bg-primary text-primary-foreground";
    if (current) return "bg-accent text-accent-foreground";
    return "bg-muted text-muted-foreground";
}

/**
 * Compute the "Illustrating page X of 24…" live sub-label from either the
 * server-published progress counters (preferred) or, as a fallback, from the
 * per-page image_status on the pages array.
 */
function computeImageProgress(order) {
    if (!order) return null;
    const totalFromOrder = order.image_progress_total || 0;
    const doneFromOrder = order.image_progress || 0;
    const pagesArr = Array.isArray(order.pages) ? order.pages : [];
    const total = totalFromOrder || pagesArr.length || 24;
    const doneFromPages = pagesArr.filter(
        (p) => p.image_status === "done" || p.image_status === "placeholder",
    ).length;
    // Trust the higher of the two — pages array can lag DB writes by one poll
    const done = Math.max(doneFromOrder, doneFromPages);
    const current =
        order.image_progress_current_page ||
        (pagesArr.find((p) => p.image_status === "generating") || {}).page_number ||
        null;
    return { done, total, current };
}

export function OrderProgressTimeline({ order }) {
    const status = order?.status || "";
    const stageIdx = statusToIndex(status);
    const isImagesStage = status === "images_generating";
    const progress = isImagesStage ? computeImageProgress(order) : null;

    return (
        <div className="paper-card rounded-3xl p-6 md:p-8 mb-12">
            <h2 className="font-serif text-2xl text-primary mb-6">Forging your book…</h2>
            <ol className="space-y-3">
                {STAGES.slice(0, 9).map((s, i) => {
                    const done = i < stageIdx;
                    const current = i === stageIdx;
                    const showImageLive = current && s.key === "images_generating" && progress;
                    return (
                        <li key={s.key} className="flex items-start gap-3">
                            <div className={`w-6 h-6 mt-0.5 shrink-0 rounded-full flex items-center justify-center transition-colors ${stageDotClass(done, current)}`}>
                                <StageIcon done={done} current={current} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <span className={done || current ? "text-foreground" : "text-muted-foreground"}>
                                    {s.label}
                                </span>
                                {showImageLive && (
                                    <div
                                        data-testid="illustration-progress"
                                        className="mt-2 space-y-2"
                                    >
                                        <div className="flex items-center justify-between text-sm">
                                            <span
                                                data-testid="illustration-progress-label"
                                                className="font-serif italic text-accent-foreground/90"
                                            >
                                                Illustrating page {progress.current || progress.done + 1} of {progress.total}…
                                            </span>
                                            <span
                                                data-testid="illustration-progress-count"
                                                className="text-xs tabular-nums text-muted-foreground"
                                            >
                                                {progress.done}/{progress.total}
                                            </span>
                                        </div>
                                        <div className="h-2 rounded-full bg-muted overflow-hidden">
                                            <div
                                                data-testid="illustration-progress-bar"
                                                className="h-full bg-accent transition-[width] duration-700 ease-out"
                                                style={{ width: `${Math.min(100, (progress.done / Math.max(1, progress.total)) * 100)}%` }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ol>
            <p className="text-xs text-muted-foreground mt-6 italic">
                Illustrations are paced one at a time (~12 s each) so we stay within the image API&apos;s rate limits. Total time is usually 8–12 minutes. You can safely close this tab — we&apos;ll keep going.
            </p>
        </div>
    );
}
